import React,{useState,useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import axios from 'axios';

const {FiUserPlus,FiSave,FiUpload,FiEdit,FiUserCheck,FiDollarSign,FiPercent,FiAward,FiX,FiUser}=FiIcons;

const DoctorSettings=()=> {
  const navigate=useNavigate();
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [success,setSuccess]=useState(false);
  const [error,setError]=useState('');
  const [doctors,setDoctors]=useState([]);
  const [showModal,setShowModal]=useState(false);
  const [modalLoading,setModalLoading]=useState(false);
  const [profileImagePreview,setProfileImagePreview]=useState('');
  const [profileImageFile,setProfileImageFile]=useState(null);
  const [selectedDoctorId,setSelectedDoctorId]=useState(null);
  const [isCreating,setIsCreating]=useState(false);
  const [formData,setFormData]=useState({
    user_id: '',
    name: '',
    email: '',
    contact: '',
    is_specialist: false,
    general_payout_percentage: 35.00,
    specialist_payout_percentage: 40.00,
    qualification: '',
    specialization: '',
    bio: '',
    active: true
  });

  useEffect(()=> {
    fetchDoctors();
  },[]);

  // Enhanced debug function
  const debugLog=(message,data=null)=> {
    console.log(`[DoctorSettings] ${message}`,data || '');
  };

  const fetchDoctors=async ()=> {
    try {
      debugLog('Fetching doctors list...');
      setLoading(true);
      const response=await axios.get('/api/settings/doctors');
      debugLog('Doctors API response:',response);
      debugLog(`Doctors fetched successfully: ${response.data.length} doctors found`);
      setDoctors(response.data);
    } catch (error) {
      debugLog('Error fetching doctors:',error);
      setError('Failed to load doctors');
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctorDetails=async (doctorId)=> {
    try {
      debugLog(`Fetching details for doctor ID: ${doctorId}`);
      setModalLoading(true);
      setError('');

      const response=await axios.get(`/api/settings/doctors/${doctorId}`);
      debugLog('Doctor details API response:',response);
      debugLog('Doctor details fetched successfully:',response.data);

      // Set form data with retrieved doctor details
      setFormData({
        user_id: response.data.user_id || '',
        name: response.data.name || '',
        email: response.data.email || '',
        contact: response.data.contact || '',
        is_specialist: response.data.is_specialist === 1,
        general_payout_percentage: parseFloat(response.data.general_payout_percentage) || 35.00,
        specialist_payout_percentage: parseFloat(response.data.specialist_payout_percentage) || 40.00,
        qualification: response.data.qualification || '',
        specialization: response.data.specialization || '',
        bio: response.data.bio || '',
        active: response.data.active !== 0
      });

      // Set profile image preview if available
      if (response.data.profile_image) {
        debugLog('Profile image found:',response.data.profile_image);
        setProfileImagePreview(response.data.profile_image);
      } else {
        debugLog('No profile image found');
        setProfileImagePreview('');
      }
    } catch (error) {
      debugLog('Error fetching doctor details:',error);
      setError(`Failed to load doctor details: ${error.response?.data?.error || error.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  const handleInputChange=(e)=> {
    const {name,value,type,checked}=e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleProfileImageChange=(e)=> {
    const file=e.target.files[0];
    if (file) {
      debugLog('Profile image selected:',file.name);
      setProfileImageFile(file);
      const reader=new FileReader();
      reader.onloadend=()=> {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit=async (e)=> {
    e.preventDefault();
    setError('');
    setSaving(true);
    setSuccess(false);

    try {
      debugLog(`Saving doctor settings for doctor ID: ${selectedDoctorId}`);
      
      // Create FormData object for file upload
      const data=new FormData();
      
      // Add all form fields
      data.append('name',formData.name);
      data.append('email',formData.email || '');
      data.append('contact',formData.contact || '');
      data.append('is_specialist',formData.is_specialist ? 1 : 0);
      data.append('general_payout_percentage',formData.general_payout_percentage);
      data.append('specialist_payout_percentage',formData.specialist_payout_percentage);
      data.append('qualification',formData.qualification || '');
      data.append('specialization',formData.specialization || '');
      data.append('bio',formData.bio || '');
      data.append('active',formData.active ? 1 : 0);

      // Add profile image file if selected
      if (profileImageFile) {
        data.append('profile_image',profileImageFile);
        debugLog('Added profile image to form data');
      }

      let response;
      if (isCreating) {
        debugLog('Creating new doctor...');
        response=await axios.post('/api/settings/doctors',data,{
          headers: {'Content-Type': 'multipart/form-data'}
        });
      } else {
        debugLog('Updating existing doctor...');
        response=await axios.put(`/api/settings/doctors/${selectedDoctorId}`,data,{
          headers: {'Content-Type': 'multipart/form-data'}
        });
      }

      debugLog('Doctor saved successfully:',response.data);
      setSuccess(true);
      fetchDoctors(); // Refresh the doctors list

      // Clear success message after 3 seconds and close modal
      setTimeout(()=> {
        setSuccess(false);
        closeModal();
      },3000);
    } catch (error) {
      debugLog('Error saving doctor settings:',error);
      setError(error.response?.data?.error || 'Failed to save doctor settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const openCreateModal=()=> {
    debugLog('Opening create doctor modal');
    setIsCreating(true);
    setSelectedDoctorId(null);
    setFormData({
      user_id: '',
      name: '',
      email: '',
      contact: '',
      is_specialist: false,
      general_payout_percentage: 35.00,
      specialist_payout_percentage: 40.00,
      qualification: '',
      specialization: '',
      bio: '',
      active: true
    });
    setProfileImagePreview('');
    setProfileImageFile(null);
    setError('');
    setSuccess(false);
    setShowModal(true);
  };

  const openEditModal=(doctorId)=> {
    debugLog(`Opening edit modal for doctor ID: ${doctorId}`);
    setIsCreating(false);
    setSelectedDoctorId(doctorId);
    setProfileImagePreview('');
    setProfileImageFile(null);
    setError('');
    setSuccess(false);
    fetchDoctorDetails(doctorId);
    setShowModal(true);
  };

  const closeModal=()=> {
    debugLog('Closing doctor modal');
    setShowModal(false);
    setSelectedDoctorId(null);
    setIsCreating(false);
    setFormData({
      user_id: '',
      name: '',
      email: '',
      contact: '',
      is_specialist: false,
      general_payout_percentage: 35.00,
      specialist_payout_percentage: 40.00,
      qualification: '',
      specialization: '',
      bio: '',
      active: true
    });
    setProfileImagePreview('');
    setProfileImageFile(null);
    setError('');
  };

  // Helper function to get user initials for avatar
  const getUserInitials=(name)=> {
    if (!name) return "";
    return name
      .split(' ')
      .map(n=> n[0])
      .join('')
      .substring(0,2)
      .toUpperCase();
  };

  if (loading && !showModal) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      transition={{duration: 0.3}}
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-grey-900">Doctor Management</h2>
            <p className="mt-1 text-sm text-grey-600">
              Configure doctor profiles, specializations and payout percentages.
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <SafeIcon icon={FiUserPlus} className="w-4 h-4 mr-2" />
              Add Doctor
            </button>
            <button
              onClick={()=> navigate('/settings/users')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500"
            >
              <SafeIcon icon={FiUserPlus} className="w-4 h-4 mr-2" />
              Add New User
            </button>
          </div>
        </div>

        {/* Doctor List */}
        <div className="bg-white shadow overflow-hidden rounded-md">
          <ul className="divide-y divide-grey-200">
            {doctors.length === 0 ? (
              <li className="px-6 py-12 text-center">
                <SafeIcon icon={FiUserCheck} className="mx-auto h-12 w-12 text-grey-400" />
                <h3 className="mt-2 text-sm font-medium text-grey-900">No doctors found</h3>
                <p className="mt-1 text-sm text-grey-500">
                  Add doctors from the User Management page or create doctors without user accounts.
                </p>
              </li>
            ) : (
              doctors.map((doctor)=> (
                <li key={doctor.id} className={`px-6 py-5 hover:bg-grey-50 ${!doctor.active ? 'opacity-60' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {doctor.profile_image ? (
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={doctor.profile_image}
                            alt={doctor.name}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-maroon-100 flex items-center justify-center">
                            <span className="text-maroon-800 font-medium text-sm">
                              {getUserInitials(doctor.name)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="font-medium text-grey-900 flex items-center">
                          {doctor.name}
                          {!doctor.user_id && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-grey-100 text-grey-800">
                              No User Account
                            </span>
                          )}
                          {!doctor.active && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-grey-500">
                          {doctor.specialization || 'General Dentistry'}
                          {doctor.email && ` • ${doctor.email}`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {doctor.is_specialist && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <SafeIcon icon={FiAward} className="w-3 h-3 mr-1" />
                          Specialist
                        </span>
                      )}
                      <button
                        onClick={()=> openEditModal(doctor.id)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-maroon-700 bg-maroon-100 hover:bg-maroon-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500"
                      >
                        <SafeIcon icon={FiEdit} className="w-4 h-4 mr-1" />
                        Edit
                      </button>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      {/* Create/Edit Doctor Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{scale: 0.95,opacity: 0}}
            animate={{scale: 1,opacity: 1}}
            className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-screen overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-grey-900">
                {isCreating ? 'Add New Doctor' : 'Edit Doctor Profile'}
                {selectedDoctorId && ` (ID: ${selectedDoctorId})`}
              </h3>
              <button
                onClick={closeModal}
                className="text-grey-400 hover:text-grey-500"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <SafeIcon icon={FiIcons.FiAlertTriangle} className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <SafeIcon icon={FiIcons.FiCheckCircle} className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700">
                      {isCreating ? 'Doctor created successfully!' : 'Doctor profile updated successfully!'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {modalLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-maroon-600"></div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Doctor Profile */}
                <div className="bg-white rounded-lg border border-grey-200 overflow-hidden">
                  <div className="px-4 py-5 sm:p-6 space-y-6">
                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <label htmlFor="name" className="block text-sm font-medium text-grey-700">
                          Doctor Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          required
                          className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                          value={formData.name}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="email" className="block text-sm font-medium text-grey-700">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="contact" className="block text-sm font-medium text-grey-700">
                          Contact Number
                        </label>
                        <input
                          type="text"
                          name="contact"
                          id="contact"
                          className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                          value={formData.contact}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="sm:col-span-6">
                        <label htmlFor="profile_image" className="block text-sm font-medium text-grey-700">
                          Profile Photo
                        </label>
                        <div className="mt-1 flex items-center">
                          <div className="flex-shrink-0 h-16 w-16 border border-grey-300 rounded-full overflow-hidden bg-grey-100">
                            {profileImagePreview ? (
                              <img
                                src={profileImagePreview}
                                alt="Profile preview"
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-grey-400">
                                <SafeIcon icon={FiUser} className="h-8 w-8" />
                              </div>
                            )}
                          </div>
                          <div className="ml-5">
                            <label
                              htmlFor="profile-upload"
                              className="relative cursor-pointer bg-white rounded-md font-medium text-maroon-600 hover:text-maroon-500 focus-within:outline-none"
                            >
                              <span className="flex items-center space-x-1">
                                <SafeIcon icon={FiUpload} className="w-4 h-4" />
                                <span>Upload a photo</span>
                              </span>
                              <input
                                id="profile-upload"
                                name="profile-upload"
                                type="file"
                                className="sr-only"
                                accept="image/*"
                                onChange={handleProfileImageChange}
                              />
                            </label>
                            <p className="text-xs text-grey-500 mt-1">PNG, JPG, GIF up to 5MB</p>
                          </div>
                        </div>
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="qualification" className="block text-sm font-medium text-grey-700">
                          Qualification
                        </label>
                        <input
                          type="text"
                          name="qualification"
                          id="qualification"
                          className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                          value={formData.qualification}
                          onChange={handleInputChange}
                          placeholder="e.g. DDS, University of Dental Medicine"
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="specialization" className="block text-sm font-medium text-grey-700">
                          Specialization
                        </label>
                        <input
                          type="text"
                          name="specialization"
                          id="specialization"
                          className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                          value={formData.specialization}
                          onChange={handleInputChange}
                          placeholder="e.g. Orthodontics, Cosmetic Dentistry"
                        />
                      </div>

                      <div className="sm:col-span-6">
                        <label htmlFor="bio" className="block text-sm font-medium text-grey-700">
                          Bio
                        </label>
                        <textarea
                          id="bio"
                          name="bio"
                          rows={3}
                          className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                          value={formData.bio}
                          onChange={handleInputChange}
                          placeholder="Doctor's professional background and expertise..."
                        />
                      </div>

                      <div className="sm:col-span-6">
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="active"
                              name="active"
                              type="checkbox"
                              className="focus:ring-maroon-500 h-4 w-4 text-maroon-600 border-grey-300 rounded"
                              checked={formData.active}
                              onChange={handleInputChange}
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="active" className="font-medium text-grey-700">
                              Active
                            </label>
                            <p className="text-grey-500">
                              Inactive doctors will not appear in appointment scheduling.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payout Settings */}
                <div className="bg-white rounded-lg border border-grey-200 overflow-hidden">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center mb-4">
                      <SafeIcon icon={FiDollarSign} className="h-5 w-5 text-grey-400 mr-2" />
                      <h3 className="text-lg font-medium leading-6 text-grey-900">Payout Settings</h3>
                    </div>
                    <div className="space-y-6">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="is_specialist"
                            name="is_specialist"
                            type="checkbox"
                            className="focus:ring-maroon-500 h-4 w-4 text-maroon-600 border-grey-300 rounded"
                            checked={formData.is_specialist}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="is_specialist" className="font-medium text-grey-700">
                            Specialist Doctor
                          </label>
                          <p className="text-grey-500">
                            Specialist doctors have different payout percentages for specialist treatments.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        <div className="sm:col-span-3">
                          <label htmlFor="general_payout_percentage" className="block text-sm font-medium text-grey-700">
                            General Payout Percentage
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <SafeIcon icon={FiPercent} className="text-grey-400 h-5 w-5" />
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              name="general_payout_percentage"
                              id="general_payout_percentage"
                              className="focus:ring-maroon-500 focus:border-maroon-500 block w-full pl-10 pr-12 sm:text-sm border-grey-300 rounded-md"
                              value={formData.general_payout_percentage}
                              onChange={handleInputChange}
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                              <span className="text-grey-500 sm:text-sm">%</span>
                            </div>
                          </div>
                          <p className="mt-2 text-sm text-grey-500">
                            Percentage of the invoice amount for general treatments.
                          </p>
                        </div>

                        {formData.is_specialist && (
                          <div className="sm:col-span-3">
                            <label htmlFor="specialist_payout_percentage" className="block text-sm font-medium text-grey-700">
                              Specialist Payout Percentage
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <SafeIcon icon={FiPercent} className="text-grey-400 h-5 w-5" />
                              </div>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                name="specialist_payout_percentage"
                                id="specialist_payout_percentage"
                                className="focus:ring-maroon-500 focus:border-maroon-500 block w-full pl-10 pr-12 sm:text-sm border-grey-300 rounded-md"
                                value={formData.specialist_payout_percentage}
                                onChange={handleInputChange}
                              />
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <span className="text-grey-500 sm:text-sm">%</span>
                              </div>
                            </div>
                            <p className="mt-2 text-sm text-grey-500">
                              Percentage of the invoice amount for specialist treatments (codes starting with SPC-).
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="mr-3 px-4 py-2 border border-grey-300 shadow-sm text-sm font-medium rounded-md text-grey-700 bg-white hover:bg-grey-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                        {isCreating ? 'Create Doctor' : 'Save Doctor Profile'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default DoctorSettings;